(() => {
  if (window.MahveenFitLoaded) return;
  window.MahveenFitLoaded = true;

  const DEVICE_KEY = 'mahveen.measurements.v1';
  const WISHLIST_KEY = 'mahveen.wishlist.v1';
  const PENDING_KEY = 'mahveen.measurements.pending.v1';
  const API_PATH = '/apps/mahveen-fit';
  const safeJson = (value, fallback) => {
    if (!value || value.length > 100000) return fallback;
    try { return JSON.parse(value); } catch (_) { return fallback; }
  };
  const readStorage = (key, fallback) => safeJson(localStorage.getItem(key), fallback);
  const writeStorage = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; }
  };
  const text = (value, max = 120) => String(value || '').trim().slice(0, max);
  const numeric = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= 1 && number <= 300 ? Math.round(number * 10) / 10 : null;
  };
  const proxy = async (resource, method = 'GET', body) => {
    const response = await fetch(`${API_PATH}?resource=${encodeURIComponent(resource)}`, {
      method,
      credentials: 'same-origin',
      headers: body ? {'Content-Type': 'application/json'} : undefined,
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) throw new Error(response.status === 401 ? 'Please sign in first.' : 'Could not save right now.');
    return response.json();
  };

  // Measurement help supports pointer, keyboard and touch interactions.
  let helpButton;
  let helpBubble;
  const closeHelp = () => {
    helpButton?.removeAttribute('aria-describedby');
    helpButton = null;
    if (helpBubble) helpBubble.hidden = true;
  };
  const openHelp = (button) => {
    const message = button.dataset.mfHelp || button.getAttribute('title');
    if (!message) return;
    button.dataset.mfHelp = message;
    button.removeAttribute('title');
    closeHelp();
    if (!helpBubble) {
      helpBubble = document.createElement('div');
      helpBubble.id = 'mahveen-measurement-help';
      helpBubble.className = 'mahveen-fit__tooltip';
      helpBubble.setAttribute('role', 'tooltip');
      document.body.append(helpBubble);
    }
    helpButton = button;
    helpBubble.textContent = message;
    helpBubble.hidden = false;
    button.setAttribute('aria-describedby', helpBubble.id);
    const rect = button.getBoundingClientRect();
    const bubble = helpBubble.getBoundingClientRect();
    helpBubble.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - bubble.width - 8))}px`;
    helpBubble.style.top = `${rect.bottom + bubble.height + 16 > window.innerHeight ? Math.max(8, rect.top - bubble.height - 8) : rect.bottom + 8}px`;
  };
  document.addEventListener('pointerover', (event) => {
    const button = event.target.closest('.mahveen-fit__help');
    if (button) openHelp(button);
  });
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'touch' && event.target.closest('.mahveen-fit__help') && !event.target.contains(event.relatedTarget)) closeHelp();
  });
  document.addEventListener('focusin', (event) => {
    const button = event.target.closest('.mahveen-fit__help');
    if (button) openHelp(button); else closeHelp();
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.mahveen-fit__help');
    if (button) { event.preventDefault(); openHelp(button); } else closeHelp();
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeHelp(); });
  window.addEventListener('scroll', closeHelp, true);
  window.addEventListener('resize', closeHelp);

  function initFit(root) {
    if (root.dataset.mfReady) return;
    root.dataset.mfReady = 'true';
    const form = document.getElementById(root.dataset.formId) || root.closest('.product-single')?.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const custom = root.querySelector('[data-mf-custom]');
    const radios = [...root.querySelectorAll('input[type="radio"][name^="mf-size-"]')];
    const fields = [...root.querySelectorAll('[data-mf-measurement]')];
    const customProperties = [...root.querySelectorAll('[data-mf-custom-property]')];
    const confirm = root.querySelector('[data-mf-confirm]');
    const sizeProperty = root.querySelector('[data-mf-property="size"]');
    const profileProperty = root.querySelector('[data-mf-property="profile"]');
    const unitProperty = root.querySelector('[data-mf-property="unit"]');
    const unit = root.querySelector('[data-mf-unit]');
    const error = root.querySelector('[data-mf-error]');
    const remember = root.querySelector('[data-mf-remember-device]');
    const clear = root.querySelector('[data-mf-clear-device]');
    const signin = root.querySelector('[data-mf-signin]');
    const saveAccount = root.querySelector('[data-mf-save-account]');
    const profileNameWrap = root.querySelector('[data-mf-profile-name-wrap]');
    const profileName = root.querySelector('[data-mf-profile-name]');
    const profilePicker = root.querySelector('[data-mf-profile-picker]');
    const profileSelect = root.querySelector('[data-mf-profile-select]');
    const status = root.querySelector('[data-mf-account-status]');
    const notes = root.querySelector('[data-mf-notes]');
    const appSubmit = root.querySelector('[data-mf-submit]');
    const appSubmitText = root.querySelector('[data-mf-submit-text]');
    const productSection = root.closest('[data-section-type="product-single"]');
    const nativeSize = [...(productSection?.querySelectorAll('variant-radios fieldset') || [])]
      .find((fieldset) => /^size$/i.test(fieldset.querySelector('legend')?.textContent.trim() || ''));
    if (nativeSize) {
      nativeSize.classList.add('mf-native-size');
      const chart = nativeSize.querySelector('.product-form__popup-link');
      if (chart) {
        chart.textContent = 'SIZE CHART';
        // Do not open a blank measurement chart when no page is configured.
        if (!chart.getAttribute('href')) {
          chart.removeAttribute('href');
          chart.classList.remove('js-product-popup-trigger');
          chart.setAttribute('aria-disabled', 'true');
          chart.title = 'Size chart measurements are not available yet.';
        }
      }
    }
    const syncSizeVariant = (selected) => {
      const input = [...(nativeSize?.querySelectorAll('input[type="radio"]') || [])]
        .find((option) => option.value === selected);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', {bubbles: true}));
      }
    };
    const nativeSubmit = form.querySelector('[type="submit"][name="add"], .js-product-add');
    if (!appSubmit) return;
    form.classList.add('mf-buy-now-hidden');
    let productAvailable = root.dataset.productAvailable === 'true' && !nativeSubmit?.disabled;
    nativeSubmit?.classList.add('mf-original-add-hidden');
    let profiles = [];

    const fieldValue = (field) => {
      if (field.dataset.mfValueType === 'long-text') return text(field.value, 300);
      if (field.dataset.mfValueType === 'text') return text(field.value, 40);
      return numeric(field.value);
    };
    const values = () => Object.fromEntries(fields.map((field) => [field.dataset.mfMeasurement, fieldValue(field)]));
    const deviceValues = () => Object.fromEntries(
      Object.entries(values()).filter(([key]) => key !== 'dress_color')
    );
    const applyValues = (measurements, selectedUnit) => {
      fields.forEach((field) => { field.value = measurements?.[field.dataset.mfMeasurement] ?? ''; });
      if (selectedUnit === 'cm' || selectedUnit === 'in') unit.value = selectedUnit;
      validate(false);
    };
    const customSelected = () => radios.find((radio) => radio.checked)?.value === 'Custom';
    let validationAttempted = false;
    const validate = (show) => {
      if (show) validationAttempted = true;
      if (!customSelected()) {
        error.hidden = true;
        appSubmit.disabled = !productAvailable;
        return true;
      }
      const invalid = fields.filter((field) => field.dataset.mfRequired === 'true' && (field.dataset.mfValueType ? !text(field.value, 40) : numeric(field.value) === null));
      fields.forEach((field) => field.setAttribute('aria-invalid', validationAttempted && invalid.includes(field) ? 'true' : 'false'));
      const valid = invalid.length === 0 && confirm.checked;
      appSubmit.disabled = !productAvailable;
      if (validationAttempted && !valid) {
        error.textContent = invalid.length ? 'Complete every required measurement and selection.' : 'Please confirm that you reviewed the measurements.';
        error.hidden = false;
      } else if (valid) error.hidden = true;
      return valid;
    };
    const setMode = (selected) => {
      const isCustom = selected === 'Custom';
      custom.hidden = !isCustom;
      form.classList.toggle('mf-custom-active', isCustom);
      sizeProperty.value = selected;
      fields.forEach((field) => { field.disabled = !isCustom; field.required = isCustom && field.dataset.mfRequired === 'true'; });
      customProperties.forEach((property) => { property.disabled = !isCustom; });
      if (notes) notes.disabled = !isCustom;
      profileProperty.disabled = !isCustom;
      unitProperty.disabled = !isCustom;
      validate(false);
    };
    radios.forEach((radio) => radio.addEventListener('change', () => {
      syncSizeVariant(radio.value);
      setMode(radio.value);
    }));
    fields.forEach((field) => {
      if (field.type === 'number') {
        field.addEventListener('keydown', (event) => {
          if (['e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
        });
      }
      field.addEventListener('input', () => {
        if (field.type === 'number') {
          const parts = field.value.replace(/[^\d.]/g, '').split('.');
          const sanitized = parts.length > 1 ? `${parts.shift()}.${parts.join('')}` : parts[0];
          if (field.value !== sanitized) field.value = sanitized;
        }
        validate(false);
      });
      field.addEventListener('change', () => validate(false));
    });
    confirm.addEventListener('change', () => validate(false));
    unit.addEventListener('change', () => { unitProperty.value = unit.value; });
    if (nativeSubmit) {
      const syncNativeButton = () => {
        productAvailable = !nativeSubmit.disabled;
        const nativeText = nativeSubmit.querySelector('.js-product-add-text')?.textContent?.trim();
        appSubmitText.textContent = nativeText || (productAvailable ? 'Add to cart' : 'Sold out');
        validate(false);
      };
      new MutationObserver(syncNativeButton).observe(nativeSubmit, {attributes: true, childList: true, subtree: true, characterData: true});
      syncNativeButton();
    }
    const selectedNativeSize = nativeSize?.querySelector('input:checked')?.value;
    const initialRadio = radios.find((radio) => radio.value === selectedNativeSize);
    if (initialRadio) initialRadio.checked = true;
    setMode(radios.find((radio) => radio.checked)?.value || 'XS');

    const saved = readStorage(DEVICE_KEY, null);
    if (saved?.expiresAt > Date.now() && saved.measurements) {
      applyValues({...saved.measurements, dress_color: ''}, saved.unit);
      if (remember) remember.checked = true;
      if (clear) clear.hidden = false;
    } else if (saved) localStorage.removeItem(DEVICE_KEY);

    clear?.addEventListener('click', () => {
      localStorage.removeItem(DEVICE_KEY);
      applyValues({}, unit.value);
      remember.checked = false;
      clear.hidden = true;
    });
    signin?.addEventListener('click', () => {
      writeStorage(PENDING_KEY, {measurements: values(), unit: unit.value, createdAt: Date.now()});
    });
    saveAccount?.addEventListener('change', () => { profileNameWrap.hidden = !saveAccount.checked; });

    const renderProfiles = () => {
      if (!profileSelect) return;
      profileSelect.querySelectorAll('option:not(:first-child)').forEach((option) => option.remove());
      profiles.forEach((profile) => {
        const option = document.createElement('option');
        option.value = profile.id;
        option.textContent = profile.name;
        profileSelect.append(option);
      });
      profilePicker.hidden = profiles.length === 0;
    };
    profileSelect?.addEventListener('change', () => {
      const profile = profiles.find((item) => item.id === profileSelect.value);
      profileProperty.value = profile?.name || '';
      if (profile) applyValues(profile.measurements, profile.unit);
    });

    if (root.dataset.customerId) {
      proxy('profiles').then((data) => {
        profiles = Array.isArray(data.profiles) ? data.profiles : [];
        renderProfiles();
        const pending = readStorage(PENDING_KEY, null);
        if (pending?.createdAt > Date.now() - 3600000) {
          applyValues(pending.measurements, pending.unit);
          saveAccount.checked = true;
          profileNameWrap.hidden = false;
          status.textContent = 'Your measurements from before sign-in are ready. Add a profile name to save them.';
          localStorage.removeItem(PENDING_KEY);
        }
      }).catch(() => {
        if (saveAccount) saveAccount.disabled = true;
        if (profileNameWrap) profileNameWrap.hidden = true;
        if (status) status.textContent = 'Saved profiles will be available after the account app is connected.';
      });
    }

    appSubmit.addEventListener('click', (event) => {
      if (appSubmit.classList.contains('is-adding')) { event.preventDefault(); return; }
      if (customSelected() && !validate(true)) {
        event.preventDefault();
        error.scrollIntoView({behavior: 'smooth', block: 'center'});
      }
    });
    form.addEventListener('submit', async (event) => {
      // Creative's cart controller must animate/report errors on the visible button.
      const productForm = form.closest('product-form');
      if (productForm) productForm.submitButton = appSubmit;
      if (!customSelected()) return;
      if (!validate(true)) { event.preventDefault(); event.stopImmediatePropagation(); error.scrollIntoView({behavior: 'smooth', block: 'center'}); return; }
      unitProperty.value = unit.value;
      if (remember?.checked) {
        const days = Math.min(365, Math.max(30, Number(root.dataset.storageDays) || 365));
        writeStorage(DEVICE_KEY, {version: 1, unit: unit.value, measurements: deviceValues(), expiresAt: Date.now() + days * 86400000});
      }
      if (saveAccount?.checked) {
        event.stopImmediatePropagation();
        const name = text(profileName.value, 40);
        if (!name) {
          event.preventDefault();
          error.textContent = 'Enter a name for this measurement profile.';
          error.hidden = false;
          profileName.focus();
          return;
        }
        event.preventDefault();
        status.textContent = 'Saving profile…';
        try {
          const profile = {id: profileSelect?.value || crypto.randomUUID(), name, unit: unit.value, measurements: values(), updatedAt: new Date().toISOString()};
          const next = [...profiles.filter((item) => item.id !== profile.id), profile].slice(-10);
          await proxy('profiles', 'POST', {profiles: next});
          profiles = next;
          profileProperty.value = name;
          renderProfiles();
          status.textContent = 'Profile saved. Adding to cart…';
          saveAccount.checked = false;
          form.requestSubmit(event.submitter || undefined);
        } catch (saveError) {
          status.textContent = '';
          error.textContent = saveError.message;
          error.hidden = false;
        }
      }
    }, true);
  }

  const wishlist = {
    items: readStorage(WISHLIST_KEY, []),
    customer: false,
    save() { this.items = this.items.slice(-100); writeStorage(WISHLIST_KEY, this.items); updateWishlistUi(); if (this.customer) proxy('wishlist', 'POST', {items: this.items}).catch(() => {}); },
    has(productId) { return this.items.some((item) => item.productId === String(productId)); },
    remove(productId) { this.items = this.items.filter((item) => item.productId !== String(productId)); this.save(); },
    add(item) { this.items = [...this.items.filter((old) => old.productId !== item.productId), item]; this.save(); }
  };

  async function productFromCard(card) {
    const productId = String(card.dataset.productId || '');
    const link = card.querySelector('a[product-card-link]');
    const url = link?.href || '';
    const title = text(link?.title || card.querySelector('product-card-title')?.textContent, 160);
    const image = card.querySelector('img')?.currentSrc || card.querySelector('img')?.src || '';
    let variantId = card.querySelector('input[name="id"]')?.value || '';
    let available = !card.classList.contains('product-card--sold-out');
    if (!variantId && url) {
      try {
        const response = await fetch(`${url.split('?')[0]}.js`, {headers: {'Accept': 'application/json'}});
        const data = await response.json();
        variantId = String(data.variants?.find((variant) => variant.available)?.id || data.variants?.[0]?.id || '');
        available = Boolean(data.available);
      } catch (_) { available = !card.classList.contains('product-card--sold-out'); }
    }
    return {productId, variantId, title, url, image, available, addedAt: new Date().toISOString()};
  }

  function setHeart(target, active = false) {
    const source = document.querySelector('[data-mf-heart]');
    if (!source || !target) return;
    const icon = source.cloneNode(true);
    icon.removeAttribute('data-mf-heart');
    icon.setAttribute('fill', active ? 'currentColor' : 'none');
    target.replaceChildren(icon);
  }

  function decorateCards(scope = document) {
    // Product cards have no wishlist control; the product page retains it.
    scope.querySelectorAll('[data-mf-card-wishlist]').forEach((button) => button.remove());
  }

  function updateWishlistUi() {
    document.querySelectorAll('[data-mf-card-wishlist]').forEach((button) => {
      const active = wishlist.has(button.closest('product-card')?.dataset.productId);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      setHeart(button, active);
    });
    document.querySelectorAll('[data-mf-wishlist-product]').forEach((button) => {
      const root = button.closest('[data-mf-product]') || button.closest('[data-section-type="product-single"]')?.querySelector('[data-mf-product]');
      const active = wishlist.has(root?.dataset.productId);
      button.setAttribute('aria-pressed', String(active));
      setHeart(button.querySelector(':scope > span:first-child'), active);
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      button.querySelector('.mahveen-wishlist-button__text').textContent = 'WISHLIST';
    });
    document.querySelectorAll('[data-mf-wishlist-count]').forEach((count) => { count.textContent = wishlist.items.length; });
    document.querySelectorAll('[data-mf-open-wishlist]').forEach((button) => {
      button.setAttribute('aria-label', `Open wishlist (${wishlist.items.length} items)`);
    });
    renderDrawer();
  }

  function initProductWishlist() {
    document.querySelectorAll('[data-mf-wishlist-product]').forEach((button) => {
      if (button.dataset.mfReady) return;
      button.dataset.mfReady = 'true';
      const section = button.closest('[data-section-type="product-single"]');
      const root = section?.querySelector('[data-mf-product]');
      const submit = root?.querySelector('[data-mf-submit]');
      const quantityRow = section?.querySelector('.product-single__box__block--quantity_selector');
      const heading = button.closest('.mahveen-fit__heading-row');
      if (quantityRow && submit && heading) {
        quantityRow.classList.add('mf-quantity-wishlist-row');
        submit.before(quantityRow);
        quantityRow.append(heading);
      }
      button.addEventListener('click', () => {
        const root = button.closest('[data-mf-product]') || button.closest('[data-section-type="product-single"]')?.querySelector('[data-mf-product]');
        if (wishlist.has(root.dataset.productId)) return wishlist.remove(root.dataset.productId);
        const form = document.getElementById(root.dataset.formId);
        wishlist.add({
          productId: String(root.dataset.productId), variantId: form?.querySelector('[name="id"]')?.value || '',
          title: text(root.dataset.productTitle, 160), url: root.dataset.productUrl, image: root.dataset.productImage,
          available: root.dataset.productAvailable === 'true', addedAt: new Date().toISOString()
        });
      });
    });
  }

  function initDrawer() {
    const config = document.querySelector('[data-mf-wishlist-config]');
    if (!config || config.dataset.showDrawer !== 'true' || document.querySelector('[data-mf-wishlist-drawer]')) return;
    wishlist.customer = Boolean(config.dataset.customerId);
    const toggles = [...document.querySelectorAll('[data-mf-open-wishlist]')];
    const drawer = document.createElement('aside');
    drawer.className = 'mf-wishlist-drawer'; drawer.dataset.mfWishlistDrawer = ''; drawer.hidden = true;
    drawer.innerHTML = '<div class="mf-wishlist-drawer__head"><h2>Wishlist</h2><button type="button" class="mf-wishlist-drawer__close" aria-label="Close wishlist">×</button></div><div data-mf-wishlist-items></div>';
    let drawerClosing = false;
    const drawerMotion = () => ({duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 400, easing: 'cubic-bezier(.645,.045,.355,1)'});
    toggles.forEach((toggle) => toggle.addEventListener('click', async () => {
      if (!drawer.hidden || drawerClosing) return;
      drawer.hidden = false;
      drawer.animate([{transform: 'translateX(100%)'}, {transform: 'translateX(0)'}], drawerMotion());
      toggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      await refreshAvailability(); renderDrawer();
    }));
    const closeWishlist = () => {
      if (drawer.hidden || drawerClosing) return;
      drawerClosing = true;
      drawer.getAnimations().forEach(animation => animation.cancel());
      const slide = drawer.animate([{transform: 'translateX(0)'}, {transform: 'translateX(100%)'}], {...drawerMotion(), fill: 'forwards'});
      slide.finished.then(() => {
        drawer.hidden = true; slide.cancel(); drawerClosing = false;
        toggles.forEach(button => button.setAttribute('aria-expanded', 'false'));
        toggles[0]?.focus();
      });
    };
    drawer.querySelector('.mf-wishlist-drawer__close').addEventListener('click', closeWishlist);
    document.addEventListener('keydown', event => { if (event.key === 'Escape') closeWishlist(); });
    document.body.append(drawer);
    if (wishlist.customer) {
      proxy('wishlist').then((data) => {
        const remote = Array.isArray(data.items) ? data.items : [];
        const merged = new Map([...remote, ...wishlist.items].map((item) => [String(item.productId), item]));
        wishlist.items = [...merged.values()]; wishlist.save();
      }).catch(() => {});
    }
  }

  async function refreshAvailability() {
    await Promise.all(wishlist.items.map(async (item) => {
      if (!item.url) return;
      try {
        const response = await fetch(`${item.url.split('?')[0]}.js`, {headers: {'Accept': 'application/json'}});
        const data = await response.json();
        const variant = data.variants?.find((entry) => String(entry.id) === String(item.variantId)) || data.variants?.find((entry) => entry.available);
        item.available = Boolean(variant?.available); item.variantId = String(variant?.id || item.variantId || '');
      } catch (_) { item.available = Boolean(item.available); }
    }));
    writeStorage(WISHLIST_KEY, wishlist.items);
  }

  function renderDrawer() {
    const container = document.querySelector('[data-mf-wishlist-items]');
    if (!container) return;
    container.replaceChildren();
    if (!wishlist.items.length) {
      const empty = document.createElement('p'); empty.className = 'mf-wishlist-empty'; empty.textContent = 'Your wishlist is empty.'; container.append(empty); return;
    }
    wishlist.items.forEach((item) => {
      const row = document.createElement('article'); row.className = 'mf-wishlist-item';
      const image = document.createElement('img'); image.src = item.image || ''; image.alt = ''; image.loading = 'lazy';
      const body = document.createElement('div');
      const link = document.createElement('a'); link.href = item.url || '#'; link.textContent = item.title || 'Product';
      const note = document.createElement('p'); note.className = 'mf-wishlist-note'; note.textContent = item.available ? 'Available' : 'Currently unavailable';
      const actions = document.createElement('div'); actions.className = 'mf-wishlist-item__actions';
      const move = document.createElement('button'); move.type = 'button'; move.textContent = 'Move to cart'; move.disabled = !item.available || !item.variantId;
      move.addEventListener('click', async () => {
        move.disabled = true;
        try {
          const response = await fetch('/cart/add.js', {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify({items: [{id: Number(item.variantId), quantity: 1}]})});
          if (!response.ok) throw new Error(); wishlist.remove(item.productId); window.location.assign('/cart');
        } catch (_) { move.disabled = false; note.textContent = 'Could not add this item. Please open the product and try again.'; }
      });
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Remove'; remove.addEventListener('click', () => wishlist.remove(item.productId));
      actions.append(move, remove); body.append(link, note, actions); row.append(image, body); container.append(row);
    });
  }

  function boot() {
    // Allow the cart's closing slide to finish before the vendor removes it.
    if (window.jQuery) {
      window.jQuery(document).on('mfpOpen.mahveenCartTiming', () => {
        const popup = window.jQuery.magnificPopup?.instance;
        if (popup?.st?.mainClass?.includes('mfp-cart-draw')) popup.st.removalDelay = 400;
      });
    }
    document.querySelectorAll('[data-mf-product]').forEach(initFit);
    decorateCards(); initProductWishlist(); initDrawer(); updateWishlistUi();
    document.querySelectorAll('product-media-zoom .media-gallery__link').forEach((link) => {
      const imageArea = link.parentElement;
      if (imageArea.querySelector('.mf-mobile-zoom')) return;
      const zoom = document.createElement('button');
      zoom.type = 'button';
      zoom.className = 'mf-mobile-zoom';
      zoom.setAttribute('aria-label', 'Zoom product image');
      zoom.innerHTML = "<svg aria-hidden=\"true\" focusable=\"false\" role=\"presentation\" class=\"pswp__icn icon icon-plus\" xmlns=\"http://www.w3.org/2000/svg\" width=\"48\" height=\"48\" viewBox=\"0 0 48 48\"> <g fill=\"none\" fill-rule=\"evenodd\"> <path fill=\"currentColor\" d=\"M29.3137,10.2365 C35.5621,16.4849 35.5621,26.6155 29.3137,32.8639 C23.0653,39.1123 12.9347,39.1123 6.68629,32.8639 C0.437903,26.6155 0.437903,16.4849 6.68629,10.2365 C12.9347,3.98807 23.0653,3.98807 29.3137,10.2365 Z M30.7279,8.82225 C37.7574,15.8517 37.7574,27.2487 30.7279,34.2781 C23.6985,41.3075 12.3015,41.3075 5.27208,34.2781 C-1.75736,27.2487 -1.75736,15.8517 5.27208,8.82225 C12.3015,1.79281 23.6985,1.79281 30.7279,8.82225 Z M34.5779,34.9792 C34.1873,34.5886 33.5542,34.5886 33.1636,34.9792 C32.7731,35.3697 32.7731,36.0029 33.1636,36.3934 L44.4774,47.7071 C44.8679,48.0976 45.501,48.0976 45.8916,47.7071 C46.2821,47.3166 46.2821,46.6834 45.8916,46.2929 L34.5779,34.9792 Z\"/> <path class=\"pswp__zoom-icn-bar-v\" fill=\"currentColor\" d=\"M19.4858,11.9755 C19.4858,11.4367 19.0445,11 18.5,11 C17.9555,11 17.5142,11.4367 17.5142,11.9755 L17.5142,30.8043 C17.5142,31.3431 17.9555,31.7798 18.5,31.7798 C19.0445,31.7798 19.4858,31.3431 19.4858,30.8043 L19.4858,11.9755 Z\"/> <path class=\"pswp__zoom-icn-bar-h\" fill=\"currentColor\" d=\"M8.98582,20.4144 C8.44137,20.4144 8,20.8511 8,21.3899 C8,21.9286 8.44137,22.3654 8.98582,22.3654 L28.0142,22.3654 C28.5586,22.3654 29,21.9286 29,21.3899 C29,20.8511 28.5586,20.4144 28.0142,20.4144 L8.98582,20.4144 Z\"/> </g> </svg>";
      zoom.addEventListener('click', () => link.click());
      imageArea.append(zoom);
    });
    document.querySelectorAll('[data-section-type="product-single"]').forEach((section) => {
      const share = section.querySelector('.product-single__box__block--share');
      const tabs = [...section.querySelectorAll('.product-single__box__block--tab')];
      const lastTab = tabs[tabs.length - 1];
      if (share && lastTab) lastTab.after(share);
    });
    const observer = new MutationObserver((records) => {
      if (!records.some((record) => record.addedNodes.length)) return;
      decorateCards(); initProductWishlist();
    });
    observer.observe(document.body, {childList: true, subtree: true});
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot, {once: true}) : boot();
})();
